import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  DropdownPropsControlled as DropdownProps,
  Input,
  Loader,
} from '@wix/design-system';
import { CollectionViewsState } from '../../state';
import { FiltersMap } from '@wix/bex-core';
import { ViewsDropdownModals } from './ViewsDropdownModals';
import { View } from '../../model';
import { classes, st } from './ViewsDropdown.st.css.js';
import { ManageViewPopover } from './ManageViewPopover';
import {
  buildCategoryOption,
  buildViewOption,
  getViewName,
} from './viewsDropdownRenderHelpers';
import { AutoCompleteReadonly } from './AutoCompleteReadonly';
import { ViewsDropdownPrefix } from './ViewsDropdownPrefix';

export type ViewsDropdownProps<T, F extends FiltersMap> = {
  state: CollectionViewsState<T, F>;
  onSelect?: (view: View<F>) => void;
  dataHook: string;
  learnMoreUrl?: string;
  viewNamePlaceholder?: string;
  showTotal?: boolean;
} & Omit<DropdownProps, 'onSelect'>;

function _ViewsDropdown<T, F extends FiltersMap>({
  onSelect,
  dataHook,
  state,
  learnMoreUrl,
  viewNamePlaceholder,
  showTotal = true,
  dropdownWidth = '200px',
  ...dropdownProps
}: ViewsDropdownProps<T, F>) {
  const { views: viewsState } = state;
  const { currentView, collection, localSearchState } = viewsState;
  const { filteredCategoriesWithViews } = localSearchState;
  const { translate: t } = collection;
  const showLoader =
    collection.result.status.isIdle || collection.result.status.isLoading;

  const viewsOptions = useMemo(() => {
    const { viewsWithoutCategory, categories } = filteredCategoriesWithViews;

    return [
      ...viewsWithoutCategory.map((view) =>
        buildViewOption({ view, viewsState }),
      ),
      ...categories.flatMap((category) => [
        buildCategoryOption({ category, viewsState }),
        ...category.views.map((view) => buildViewOption({ view, viewsState })),
      ]),
    ];
  }, [filteredCategoriesWithViews]);

  const { options, fixedFooter } = showLoader
    ? {
        options: [],
        fixedFooter: (
          <Box
            dataHook="initial-loader"
            width="100%"
            align="center"
            verticalAlign="middle"
            padding="SP2"
          >
            <Loader size="small" />
          </Box>
        ),
      }
    : {
        options: viewsOptions,
        fixedFooter: undefined,
      };

  return (
    <Box dataHook={dataHook}>
      <Box width={dropdownWidth}>
        <AutoCompleteReadonly
          emptyStateMessage={
            !showLoader && t('cairo.viewsDropdown.nothingMatches.text')
          }
          state={state.views.autoCompleteReadonlyState}
          value={localSearchState.inputValue}
          onChange={(e) =>
            localSearchState.setInputValueAndScheduleSearch(e.target.value)
          }
          clearInput={() => localSearchState.clearSearch()}
          dropdownWidth={dropdownWidth}
          popoverProps={{ placement: 'bottom-start' }}
          {...dropdownProps}
          fixedFooter={fixedFooter}
          border="round"
          selectedId={currentView?.id}
          selectedLabel={
            currentView
              ? getViewName({ view: currentView, viewsState })
              : undefined
          }
          textOverflow="ellipsis"
          prefix={<ViewsDropdownPrefix state={state} />}
          suffix={
            !showTotal ||
            state.table.collection.showRefreshLoader ? undefined : (
              <Box className="total-count">
                <Input.Affix>
                  ({state.table.collection.result.total})
                </Input.Affix>
              </Box>
            )
          }
          className={st(classes.root)}
          dataHook="collection-views-dropdown"
          onSelect={(option) => {
            viewsState.events.emit('beforeSelectView');
            const id = option.id.toString();
            const view = viewsState.getView(id);
            if (view) {
              onSelect?.(view);
              viewsState.selectView({ id });
              viewsState.events.emit('afterSelectView', { view });
            }
            localSearchState.clearSearch();
          }}
          options={options}
          placeholder={t('cairo.viewDropdown.unsavedView.status')}
        />
      </Box>

      <Box verticalAlign="middle" paddingLeft="SP2">
        <ManageViewPopover origin="MainToolbar" state={state} />
      </Box>

      <ViewsDropdownModals
        state={state}
        learnMoreUrl={learnMoreUrl}
        viewNamePlaceholder={viewNamePlaceholder}
      />
    </Box>
  );
}

export const ViewsDropdown = observer(_ViewsDropdown);
